import dayjs from 'dayjs';
import 'dayjs/locale/ja';

import enMessages from './messages/en';
import jaMessages from './messages/ja';

export type Locale = 'en' | 'ja';

const catalogs: Record<Locale, typeof enMessages> = {
  en: enMessages,
  ja: jaMessages,
};

const parseLocale = (value: string | null | undefined): Locale | null => {
  if (value === 'ja' || value === 'en') {
    return value;
  }
  return null;
};

const rawLocale = 'en';
export let locale: Locale = parseLocale(rawLocale) ?? 'en';
export let dayjsLocale: Locale = locale;

export const setLocale = (value: string | null | undefined): Locale => {
  const parsed = 'en';
  if (parsed) {
    locale = parsed;
    dayjsLocale = parsed;
    dayjs.locale(parsed);
  }
  return locale;
};

const resolvePath = (path: string): unknown => {
  const messages = catalogs[locale];
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object' && key in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, messages as unknown);
};

export const t = (
  path: string,
  fallback = path,
  params: Record<string, string | number> = {},
): string => {
  const template = resolvePath(path);
  const value = typeof template === 'string' ? template : fallback;
  return Object.entries(params).reduce((message, [key, replacement]) => {
    return message.replace(new RegExp(`\\{${key}\\}`, 'g'), String(replacement));
  }, value);
};

export const formatDisplayDate = (date: string): string => {
  const d = dayjs(date);
  if (locale === 'ja') {
    return d.format('YYYY年M月D日');
  }
  return d.format('DD-MM-YYYY');
};

export const formatExportDate = (): string => {
  const d = dayjs();
  if (locale === 'ja') {
    return d.format('YYYY年MM月DD日');
  }
  return d.format('DD-MM-YYYY');
};

export const formatSchoolYear = (year: string | number): string => {
  return t('common.schoolYear', String(year), { year });
};

export const formatChildDevelopmentRange = (
  businessYear: string,
  endMonth?: number,
): string => {
  const yearNum = parseInt(businessYear, 10);
  if (endMonth !== undefined) {
    const monthKey = [
      'jan', 'feb', 'mar', 'apr', 'may', 'jun',
      'jul', 'aug', 'sept', 'oct', 'nov', 'dec',
    ][endMonth === 0 ? 11 : endMonth - 1];
    const endMonthLabel = t(`months.${monthKey}`);
    return t('childDevelopment.rangeLabelWithMonth', '', {
      startYear: yearNum,
      endYear: yearNum + 1,
      endMonth: endMonthLabel,
    });
  }
  return t('childDevelopment.rangeLabel', '', {
    startYear: yearNum,
    endYear: yearNum + 1,
  });
};

export const formatWorkScheduleDate = (
  year: string | number,
  month: string | number,
  day?: string | number,
): string => {
  if (locale === 'ja') {
    if (day !== undefined) {
      return t('workSchedule.dateFull', '', { year, month, day });
    }
    return t('workSchedule.dateYearMonth', '', { year, month });
  }

  if (day !== undefined) {
    return dayjs(`${year}-${month}-${day}`).format('DD-MM-YYYY');
  }
  return dayjs(`${year}-${month}-01`).format('MM-YYYY');
};

export const formatChartMonth = (month: number): string => {
  if (locale === 'ja') {
    return t('growthRecord.monthLabel', String(month), { month });
  }
  return dayjs().month(month - 1).format('MMM');
};

export const formatDayHeader = (value: string): string => {
  if (!value) {
    return '';
  }
  const d = dayjs(value);
  if (locale === 'ja') {
    return d.locale('ja').format('D dd');
  }
  return d.format('D ddd');
};

export const monthHeaderKeys = [
  'apr', 'may', 'jun', 'jul', 'aug', 'sept',
  'oct', 'nov', 'dec', 'jan', 'feb', 'mar',
] as const;

export const getMonthHeaders = (): string[] => {
  return monthHeaderKeys.map((key) => t(`months.${key}`));
};
